import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  id: "transport",
  ref: "transport_ref",
  class: "transport col-12 px-0 d-none d-md-flex my-4 font-body font-size-16 font-weight-default"
}
const _hoisted_2 = { class: "w-100" }
const _hoisted_3 = { class: "title-container d-flex justify-content-between" }
const _hoisted_4 = { class: "title-4 text-grey-800 mb-2" }
const _hoisted_5 = { class: "tabs-wrapper d-flex flex-row nowrap mt-4" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "tab-inner-wrapper" }
const _hoisted_8 = { class: "d-flex justify-content-end align-items-center" }
const _hoisted_9 = { class: "d-flex justify-content-center align-items-center" }

import { computed, ComputedRef, onMounted } from "vue"
import RadioCircle from "@/vue/common/radioCircle.vue"
import availableNonTransportPackageCodes from "@/vue/planning/computed/availableNonTransportPackageCodes"
import colors from "@/config/colors"
import { useStore } from "vuex"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import { Transport } from "@/types"


export default /*@__PURE__*/_defineComponent({
  __name: 'transportSwitch',
  setup(__props) {

const store = useStore();

class SwitchOption {
	label: string
	value: Transport | null
	selected: boolean
	constructor(label: string, value: Transport | null, selected: boolean) {
		this.label = label
		this.value = value
		this.selected = selected
	}
}

const selectedTransport = computed<Transport | null>(() => store.getters.transportv2)

const isFlightEnabled = computed(() =>
	store.getters.availablePackCodes.some((pack: { codePackage: string }) => flightPackCode.includes(pack.codePackage))
)
const isTrainEnabled = computed(() =>
	store.getters.availablePackCodes.some((pack: { codePackage: string }) => trainPackCode.includes(pack.codePackage))
)

const getDefaultPackCodeForTransport = (transport: Transport | null): string | undefined => {
	if (transport === Transport.FLIGHT) {
		return store.getters.availableFlightPackCodes[0]?.codePackage
	}
	if (transport === Transport.TRAIN) {
		return store.getters.availableTrainPackCodes[0]?.codePackage
	}
	const nonTransportPacks = store.getters.availableNonTransportPackCodes
	if (store.state.season === "E") {
		const seasonPacks = nonTransportPacks.filter(
			(pack: { codePackage: string }) =>
				pack.codePackage === "HS" || pack.codePackage === "PMA"
		)
		return seasonPacks[0]?.codePackage ?? nonTransportPacks[0]?.codePackage
	}
	return nonTransportPacks[0]?.codePackage
}

const options: ComputedRef<SwitchOption[]> = computed(() => {
	const { content } = store.state
	const switchOptions: SwitchOption[] = []
	const hasBothTransports = isFlightEnabled.value && isTrainEnabled.value

	if (isFlightEnabled.value) {
		switchOptions.push(
			new SwitchOption(
				hasBothTransports
					? (content.transport_flight || content.planner_formulaWithFlight)
					: (content.planner_formulaWithFlight || content.transport_flight),
				Transport.FLIGHT,
				selectedTransport.value === Transport.FLIGHT
			)
		)
	}

	if (isTrainEnabled.value) {
		switchOptions.push(
			new SwitchOption(
				hasBothTransports
					? (content.transport_train || content.planner_formulaWithTransport)
					: content.planner_formulaWithTransport,
				Transport.TRAIN,
				selectedTransport.value === Transport.TRAIN
			)
		)
	}

	if (availableNonTransportPackageCodes.value && availableNonTransportPackageCodes.value.length) {
		switchOptions.push(
			new SwitchOption(
				content.planner_noTransport || content.transport_none,
				null,
				selectedTransport.value === null
			)
		)
	}

	return switchOptions
})

const handleSwitch = async (transport: Transport | null) => {
	if (selectedTransport.value === transport) return

	const defaultPackCode = getDefaultPackCodeForTransport(transport)
	// UK: flights and trains are limited to 9 guests. NL: only flights are (NL TNE trains allow 20).
	const shouldOpenParticipantsDropdown =
		((store.getters.isLocaleEN && (transport === Transport.FLIGHT || transport === Transport.TRAIN)) ||
			(store.getters.isLocaleNL && transport === Transport.FLIGHT)) &&
		store.state.participants.length > 9

	if (shouldOpenParticipantsDropdown) {
		store.commit("setParticipants", store.state.participants.slice(0, 9))
	}

	store.commit("setTransportv2", transport)
	store.commit("setTransportTrue", transport !== null)
	store.commit("setTransportationSelected", transport !== null)

	if (transport === Transport.FLIGHT) {
		if (defaultPackCode) {
			await store.dispatch("setPackage", defaultPackCode)
		}
		await store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
	} else if (transport === Transport.TRAIN) {
		if (defaultPackCode) {
			// preferNative avoids PFPTT+AMS (Eurostar pack + TNE origin) when
			// switching from a flight whose airport code collides with a train station.
			await store.dispatch("setTrainPackageAndOrigin", {
				packCode: defaultPackCode,
				preferNative: true,
			})
		}
	} else {
		store.commit("setFlightOrigin", null)
		if (defaultPackCode) {
			await store.dispatch("setPackage", defaultPackCode)
		}
	}

	if (shouldOpenParticipantsDropdown) {
		setTimeout(() => {
			store.dispatch("setParticipantsDropdownOpen", true)
		}, 0)
	}
}

onMounted(() => {
	if (store.getters.transportv2 != null) return

	if (flightPackCode.includes(store.state.packCode)) {
		store.commit("setTransportv2", Transport.FLIGHT)
		store.commit("setTransportTrue", true)
	} else if (trainPackCode.includes(store.state.packCode)) {
		store.commit("setTransportv2", Transport.TRAIN)
		store.commit("setTransportTrue", true)
	} else {
		store.commit("setTransportv2", null)
		store.commit("setTransportTrue", false)
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h3", _hoisted_4, _toDisplayString("1. ") + _toDisplayString(_unref(store).state.content.planner_optForPackages), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option, option_i) => {
          return (_openBlock(), _createElementBlock("div", {
            onClick: _withModifiers(($event: any) => (handleSwitch(option.value)), ["stop"]),
            class: _normalizeClass(["tab col", 
						option.selected
							? 'active font-weight-700'
							: ''
					]),
            key: option_i
          }, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createVNode(RadioCircle, {
                  active: option.selected,
                  colorDefault: _unref(colors).darkBlue,
                  colorActive: 'white',
                  additionalClasses: 'me-1 me-xl-3'
                }, null, 8, ["active", "colorDefault"])
              ]),
              _createElementVNode("span", _hoisted_9, _toDisplayString(option.label), 1)
            ])
          ], 10, _hoisted_6))
        }), 128))
      ])
    ])
  ], 512))
}
}

})